<div class="ikigai-container">
  <!-- Paso 1: Introducción -->
  <div *ngIf="currentStep === 'introduction'" class="step-container">
    <div class="step-header">
      <h1 class="step-title">Descubre tu Ikigai</h1>
      <p class="step-description">
        Encuentra tu propósito de vida a través de la intersección de lo que amas, lo que el mundo necesita, en lo que eres bueno y por lo que te pueden pagar.
      </p>
    </div>
    
    <div class="intro-content">
      <div class="intro-card">
        <div class="intro-icon">
          <i class="fas fa-heart"></i>
        </div>
        <h3>¿Qué es el Ikigai?</h3>
        <p>Es un concepto japonés que significa "razón de ser" o "propósito de vida". Es la intersección de cuatro elementos fundamentales.</p>
      </div>
      
      <div class="intro-card">
        <div class="intro-icon">
          <i class="fas fa-compass"></i>
        </div>
        <h3>Los 4 elementos</h3>
        <p>Pasión, Misión, Vocación y Profesión. Cuando estos se alinean, encuentras tu verdadero propósito.</p>
      </div>
      
      <div class="intro-card">
        <div class="intro-icon">
          <i class="fas fa-star"></i>
        </div>
        <h3>Beneficios</h3>
        <p>Te ayuda a encontrar dirección, significado y satisfacción en tu vida personal y profesional.</p>
      </div>
    </div>
    
    <div class="step-actions">
      <button class="btn-primary" (click)="nextStep()">
        Comenzar
        <i class="fas fa-arrow-right"></i>
      </button>
    </div>
  </div>

  <!-- Pasos 2-5: Secciones del Ikigai -->
  <div *ngIf="currentStep !== 'introduction' && currentStep !== 'results'" class="step-container">
    <div class="step-header">
      <h1 class="step-title">{{ getCurrentStepTitle() }}</h1>
      <p class="step-description">
        Reflexiona sobre las siguientes preguntas para definir esta área de tu Ikigai
      </p>
      <div class="progress-bar">
        <div class="progress-fill" [style.width.%]="getProgress()"></div>
      </div>
    </div>
    
    <div class="ikigai-questions">
      <div 
        *ngFor="let question of questions[currentStep]" 
        class="question-card"
      >
        <div class="question-header">
          <div class="question-icon">
            <i class="fas fa-question-circle"></i>
          </div>
          <div class="question-content">
            <h3 class="question-title">{{ question.text }}</h3>
          </div>
        </div>
        
        <div class="question-input">
          <textarea 
            [placeholder]="question.placeholder"
            [value]="getIkigaiData(currentStep, question.id)"
            (input)="onTextareaChange(currentStep, question.id, $event)"
            class="ikigai-textarea"
            rows="4"
          ></textarea>
        </div>
      </div>
    </div>
    
    <div class="step-actions">
      <button class="btn-secondary" (click)="previousStep()">
        <i class="fas fa-arrow-left"></i>
        Anterior
      </button>
      <button 
        class="btn-primary" 
        [disabled]="!canProceedFromSection(currentStep)"
        (click)="nextStep()"
      >
        Continuar
        <i class="fas fa-arrow-right"></i>
      </button>
    </div>
  </div>

  <!-- Paso 6: Resultados -->
  <div *ngIf="currentStep === 'results'" class="step-container">
    <div class="step-header">
      <h1 class="step-title">Tu Ikigai</h1>
      <p class="step-description">
        Aquí tienes un resumen de tu reflexión sobre tu propósito de vida
      </p>
    </div>
    
    <div class="ikigai-results">
      <div class="ikigai-circle">
        <div class="ikigai-center">
          <h2>Tu Ikigai</h2>
          <p>Propósito de vida</p>
        </div>
        
        <div class="ikigai-quadrants">
          <div class="quadrant passion-quadrant">
            <div class="quadrant-icon">
              <i class="fas fa-heart"></i>
            </div>
            <h3>Pasión</h3>
            <div class="quadrant-content">
              <div *ngFor="let item of ikigaiData['passion'] | keyvalue" class="quadrant-item">
                <span class="item-text" *ngIf="item.value.trim()">{{ item.value }}</span>
              </div>
            </div>
          </div>
          
          <div class="quadrant mission-quadrant">
            <div class="quadrant-icon">
              <i class="fas fa-bullseye"></i>
            </div>
            <h3>Misión</h3>
            <div class="quadrant-content">
              <div *ngFor="let item of ikigaiData['mission'] | keyvalue" class="quadrant-item">
                <span class="item-text" *ngIf="item.value.trim()">{{ item.value }}</span>
              </div>
            </div>
          </div>
          
          <div class="quadrant vocation-quadrant">
            <div class="quadrant-icon">
              <i class="fas fa-graduation-cap"></i>
            </div>
            <h3>Vocación</h3>
            <div class="quadrant-content">
              <div *ngFor="let item of ikigaiData['vocation'] | keyvalue" class="quadrant-item">
                <span class="item-text" *ngIf="item.value.trim()">{{ item.value }}</span>
              </div>
            </div>
          </div>
          
          <div class="quadrant profession-quadrant">
            <div class="quadrant-icon">
              <i class="fas fa-briefcase"></i>
            </div>
            <h3>Profesión</h3>
            <div class="quadrant-content">
              <div *ngFor="let item of ikigaiData['profession'] | keyvalue" class="quadrant-item">
                <span class="item-text" *ngIf="item.value.trim()">{{ item.value }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="ikigai-summary">
        <h3>Resumen de tu reflexión:</h3>
        <div class="summary-sections">
          <div class="summary-section" *ngIf="hasPassionData()">
            <h4><i class="fas fa-heart"></i> Pasión</h4>
            <ul>
              <li *ngFor="let item of ikigaiData['passion'] | keyvalue">
                <span *ngIf="item.value.trim()">{{ item.value }}</span>
              </li>
            </ul>
          </div>
          
          <div class="summary-section" *ngIf="hasMissionData()">
            <h4><i class="fas fa-bullseye"></i> Misión</h4>
            <ul>
              <li *ngFor="let item of ikigaiData['mission'] | keyvalue">
                <span *ngIf="item.value.trim()">{{ item.value }}</span>
              </li>
            </ul>
          </div>
          
          <div class="summary-section" *ngIf="hasVocationData()">
            <h4><i class="fas fa-graduation-cap"></i> Vocación</h4>
            <ul>
              <li *ngFor="let item of ikigaiData['vocation'] | keyvalue">
                <span *ngIf="item.value.trim()">{{ item.value }}</span>
              </li>
            </ul>
          </div>
          
          <div class="summary-section" *ngIf="hasProfessionData()">
            <h4><i class="fas fa-briefcase"></i> Profesión</h4>
            <ul>
              <li *ngFor="let item of ikigaiData['profession'] | keyvalue">
                <span *ngIf="item.value.trim()">{{ item.value }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    
    <div class="step-actions">
      <button class="btn-secondary" (click)="restartProcess()">
        <i class="fas fa-redo"></i>
        Reiniciar
      </button>
      <button class="btn-primary" (click)="finishProcess()">
        <i class="fas fa-check"></i>
        Finalizar
      </button>
    </div>
  </div>
</div>
